"use client";

import Link from "next/link";
import type { Card } from "@/lib/catalog/grouping";
import type { Locale } from "@/lib/i18n/getLocale";
import type { PlainCatalogStrings } from "@/lib/i18n/catalog-strings";
import { usePreferences } from "@/stores/preferences";
import { useCatalogFeedback } from "@/stores/catalog-feedback";
import { PowerBuyerRow } from "./PowerBuyerRow";
import { SlideToOrder } from "./power-buyer/SlideToOrder";

export function PowerBuyerView({
  cards,
  totalCount,
  strings,
  locale = "en",
  soldOutKeys,
}: {
  cards: Card[];
  totalCount: number;
  strings?: PlainCatalogStrings;
  locale?: Locale;
  // #452: active sold-out keys, threaded to every row (each filters by its
  // own slug via the shared cascade).
  soldOutKeys?: string[];
}) {
  // Step 6.7.2 / AC171: globalUnit lives in a persisted Zustand store
  // (stores/preferences.ts) instead of local component state, so the
  // selection survives Add actions, page reloads, and nav-away/back.
  const globalUnit = usePreferences((s) => s.unitPreference);
  const setGlobalUnit = usePreferences((s) => s.setUnitPreference);
  // #468 catalog bar: adds feed the ONE shared builder bar (mounted once at the
  // catalog-page level, covering BOTH views) via the catalog-feedback store -
  // no local toast stack, no per-view bar.
  const pushAdd = useCatalogFeedback((s) => s.pushAdd);

  function pushToast(message: string, slug?: string) {
    pushAdd(message, slug, "success");
  }

  function pushWarn(message: string) {
    pushAdd(message, undefined, "warning");
  }

  return (
    <div
      data-testid="power-buyer-view"
      className="rounded-2xl border border-brand-border bg-white"
    >
      <div
        data-testid="power-buyer-toolbar"
        className="sticky top-0 z-[5] flex items-center gap-3 border-b border-brand-border bg-brand-surface px-4 py-2.5 text-[13px]"
      >
        <span className="font-medium text-brand-text-muted">{strings?.buyBy ?? "Buy by"}:</span>
        <div className="inline-flex rounded-md border border-brand-border bg-white p-0.5">
          <button
            type="button"
            data-testid="power-buyer-unit-box"
            aria-pressed={globalUnit === "box"}
            onClick={() => setGlobalUnit("box")}
            className={
              globalUnit === "box"
                ? "rounded bg-brand-teal px-3 py-1 text-[12px] font-semibold text-white"
                : "rounded px-3 py-1 text-[12px] font-semibold text-brand-text-muted hover:text-brand-text"
            }
          >
            {strings?.unitBox ?? "Box"}
          </button>
          <button
            type="button"
            data-testid="power-buyer-unit-bunch"
            aria-pressed={globalUnit === "bunch"}
            onClick={() => setGlobalUnit("bunch")}
            className={
              globalUnit === "bunch"
                ? "rounded bg-brand-teal px-3 py-1 text-[12px] font-semibold text-white"
                : "rounded px-3 py-1 text-[12px] font-semibold text-brand-text-muted hover:text-brand-text"
            }
          >
            {strings?.unitBunch ?? "Bunch"}
          </button>
        </div>
        <span
          data-testid="power-buyer-count"
          className="ml-auto text-brand-text-muted"
        >
          <strong className="text-brand-text">{cards.length}</strong> shown of{" "}
          {totalCount}
        </span>
      </div>

      {/* Sticky column header (desktop only) */}
      <div
        data-testid="power-buyer-colhead"
        className="sticky top-[48px] z-[4] hidden border-b border-brand-border bg-brand-surface px-5 py-2.5 text-[10px] font-bold uppercase tracking-wider text-brand-text-muted md:grid md:grid-cols-[40px_1.5fr_130px_130px_110px_90px] md:gap-3.5"
      >
        <div></div>
        <div>Variety</div>
        <div>Length / Color</div>
        <div className="text-right">Price</div>
        <div></div>
        <div></div>
      </div>

      <div data-testid="power-buyer-rows">
        {cards.map((c) => (
          <PowerBuyerRow
            key={c.routeSlug}
            card={c}
            globalUnit={globalUnit}
            onAdded={pushToast}
            onWarn={pushWarn}
            locale={locale}
            soldOutKeys={soldOutKeys}
          />
        ))}
        {cards.length === 0 && (
          <div
            data-testid="power-buyer-empty"
            className="px-4 py-20 text-center text-sm text-brand-text-muted"
          >
            <p className="mb-3">No varieties match the active filters.</p>
            <Link
              href="/catalog"
              className="inline-flex items-center rounded-full bg-brand-teal px-4 py-2 text-xs font-bold text-white hover:bg-brand-teal/90"
            >
              Clear all filters
            </Link>
          </div>
        )}
      </div>

      <SlideToOrder />
    </div>
  );
}
